import { Row, Table as TanstackTable } from '@tanstack/react-table';
import { useCallback } from 'react';
import {
  MultiSelectScope,
  NO_GROUP_ID,
  TableRow,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosHotkeys } from 'src/core/POS/PosHotkeys/PosHotkeys';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';

interface TableSelectAllCheckboxProps<TData> {
  showSelectAllCheckbox?: boolean;
  table: TanstackTable<TData>;
  getGroupId?: (data: TData) => string | null | undefined;
  getItemId?: (data: TData) => string;
  flattenedIdsOverride?: (number | string)[];
}

export function TableSelectAllCheckbox<TData>({
  showSelectAllCheckbox,
  table,
  getGroupId,
  getItemId,
  flattenedIdsOverride,
}: TableSelectAllCheckboxProps<TData>) {
  const { toggleSelectAll, totalItemsSelected, setGroupItems, selectionMode } =
    useMultiSelectionContext();

  const { getItemToggleState } = useMultiSelectionContext();

  // Use filtered rows instead of raw data to respect column filters
  const filteredRows = table.getFilteredRowModel().flatRows;
  const filteredData = filteredRows.map((row: Row<TData>) => row.original);

  // Check selection state for items in THIS table only (filtered rows)
  const selectedCount = filteredData.filter((item: TData, index: number) => {
    const itemId = getItemId ? getItemId(item) : String(index);
    const groupId = getGroupId
      ? (getGroupId(item) ?? NO_GROUP_ID)
      : NO_GROUP_ID;
    return getItemToggleState(itemId, groupId);
  }).length;

  const allSelected =
    selectedCount === filteredData.length && filteredData.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < filteredData.length;

  const toggleGroupWithOverride = useCallback(
    (groupId: string) => {
      if (flattenedIdsOverride) {
        if (totalItemsSelected >= flattenedIdsOverride.length) {
          setGroupItems(
            groupId,
            flattenedIdsOverride.reduce(
              (acc, id) => {
                acc[id] = false;
                return acc;
              },
              {} as Record<number | string, boolean>
            )
          );
        } else {
          setGroupItems(
            groupId,
            flattenedIdsOverride.reduce(
              (acc, id) => {
                acc[id] = true;
                return acc;
              },
              {} as Record<number | string, boolean>
            )
          );
        }
      }
    },
    [flattenedIdsOverride, setGroupItems, totalItemsSelected]
  );
  const onChange = useCallback(() => {
    if (
      selectionMode?.mode === MultiSelectScope.SingleGroup &&
      selectionMode?.groupId
    ) {
      if (flattenedIdsOverride != null) {
        toggleGroupWithOverride(selectionMode?.groupId);
      } else {
        // Select only filtered rows, not all rows
        const filteredItemIds = filteredData
          .map((item: TData) => getItemId?.(item))
          .filter((id: string | undefined) => id !== null);

        const newSelection = filteredItemIds.reduce(
          (acc: Record<string, boolean>, id?: string | undefined) => {
            if (!id) return acc;
            acc[id] = !allSelected; // Toggle: if all selected, deselect; otherwise select
            return acc;
          },
          {} as Record<string, boolean>
        );

        setGroupItems(selectionMode.groupId, newSelection);
      }
    } else if (selectionMode?.mode === MultiSelectScope.AllGroups) {
      toggleSelectAll();
    }
  }, [
    flattenedIdsOverride,
    selectionMode,
    toggleGroupWithOverride,
    toggleSelectAll,
    filteredData,
    getItemId,
    allSelected,
    setGroupItems,
  ]);

  return (
    <TableCellDiv align="left" showTooltip={false}>
      <PosHotkeys keyNames="ctrl+a, command+a" onHotkeyActivated={onChange}>
        <Checkbox
          labelPosition="right"
          indeterminate={someSelected}
          checked={allSelected || someSelected}
          onChange={onChange}
          checkboxSizeVariant="medium"
        />
      </PosHotkeys>
    </TableCellDiv>
  );
}

interface TableRowCheckboxProps<TData> {
  row: Row<TData>;
  table: TanstackTable<TData>;
  getGroupId?: (data: TData) => string | null | undefined;
  getItemId?: (data: TData) => string;
  isSelectionDisabled?: (data: TData) => string | undefined;
}

export function TableRowCheckbox<TData>({
  row,
  table,
  getGroupId,
  getItemId,
  isSelectionDisabled,
}: TableRowCheckboxProps<TData>) {
  const {
    setShiftKeyRowSelectionState,
    clearShiftKeySelection,
    getItemToggleState,
    setGroupItems,
    getGroupToggleState,
  } = useMultiSelectionContext();

  const data = row.original;
  const groupId = getGroupId ? (getGroupId(data) ?? NO_GROUP_ID) : NO_GROUP_ID;
  const itemId = getItemId ? getItemId(data) : row.id;
  const disabledReason = isSelectionDisabled
    ? isSelectionDisabled(data)
    : undefined;

  const isSelected = getItemToggleState(itemId, groupId);

  // Helper to recursively gather all item IDs from a row and its sub-rows
  const getAllItemIdsFromRow = (currentRow: Row<TData>): string[] => {
    const currentItemId = getItemId
      ? getItemId(currentRow.original)
      : currentRow.id;
    const ids = [currentItemId];

    // Recursively get IDs from sub-rows
    if (currentRow.subRows && currentRow.subRows.length > 0) {
      currentRow.subRows.forEach((subRow) => {
        ids.push(...getAllItemIdsFromRow(subRow));
      });
    }

    return ids;
  };

  const handleChange = () => {
    if (disabledReason) return;

    row.toggleSelected();
    const newState = !isSelected;
    if (newState) {
      const allRows = table.getRowModel().rows as unknown as TableRow[];
      setShiftKeyRowSelectionState(row.id, allRows, groupId);
    } else {
      clearShiftKeySelection();
    }
  };

  return (
    <TableCellDiv
      align="left"
      title={disabledReason}
      showTooltip={!!disabledReason}
      style={{ width: 'fit-content' }}
    >
      <Checkbox
        checked={isSelected}
        disabled={!!disabledReason}
        onChange={handleChange}
        checkboxSizeVariant="medium"
      />
    </TableCellDiv>
  );
}
